import { useEffect } from "react";

import { selectAuthReady } from "@/features/app/appSlice";
import { selectCurrentBuildingID } from "@/features/cartography/cartographySlice.selectors";
import { selectConfigItem } from "@/features/config/configSlice";
import { setFlightInformation } from "./flightInformationSlice";

import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useSitumAPIContext } from "@/features/situm_api/SitumAPIProvider";

const DEFAULT_POLL_INTERVAL_SECONDS = 300;

/**
 * Polls flight information from the API when the feature is enabled.
 */
export function useFlightInformationPolling(): void {
  const dispatch = useAppDispatch();
  const { getFlightsInformation, isAuthenticated } = useSitumAPIContext();
  const authReady = useAppSelector(selectAuthReady);
  const enabled = useAppSelector(
    selectConfigItem("flight_information.enabled"),
  );
  const categoryId = useAppSelector(
    selectConfigItem("flight_information.category_id"),
  );
  const pollingIntervalSeconds = useAppSelector(
    selectConfigItem("flight_information.polling_interval_seconds"),
  );
  const buildingId = useAppSelector(selectCurrentBuildingID);

  useEffect(() => {
    if (!authReady || !enabled || !categoryId || !isAuthenticated) {
      return;
    }

    let cancelled = false;

    const fetchAndUpdate = async () => {
      if (cancelled) return;

      try {
        const flights = await getFlightsInformation();
        if (!cancelled) {
          dispatch(setFlightInformation({ flights, refreshedAt: Date.now() }));
        }
      } catch (error) {
        console.warn("Failed to fetch flight information:", error);
      }
    };

    fetchAndUpdate();
    const intervalMs =
      Math.max(1, pollingIntervalSeconds ?? DEFAULT_POLL_INTERVAL_SECONDS) *
      1000;
    const intervalId = setInterval(fetchAndUpdate, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [
    authReady,
    enabled,
    categoryId,
    isAuthenticated,
    getFlightsInformation,
    pollingIntervalSeconds,
    buildingId,
    dispatch,
  ]);
}
